import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  Heart, 
  Crown, 
  Sparkles,
  FileText,
  Shield,
  CheckCircle,
  AlertCircle,
  Lock
} from 'lucide-react';
import ElegantSidebar from '../../../shared/components/layout/ElegantSidebar';
import '../styles/QRPaperCommon.css';
import { 
  ConsentPageLocationState,
  ConsentData,
  ConsentErrors,
  RoomInfo,
  QRPaperOrderApiRequest
} from '../../../shared/types/qrPaper';

const QRPaperConsentForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ConsentPageLocationState;

  const [consentData, setConsentData] = useState<ConsentData>({
    personalInfoConsent: false,
    processingConsent: false
  });
  const [consentErrors, setConsentErrors] = useState<ConsentErrors>({});
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [isLoadingRoom, setIsLoadingRoom] = useState(true);

  // リダイレクト処理とルーム情報取得
  useEffect(() => {
    if (!state?.roomid || !state?.qrUrl || !state?.selectedDesign || !state?.formData) {
      alert('注文情報が不正です。情報入力画面に戻ります。');
      navigate('/admin/qr-paper/order');
      return;
    }

    // ルーム情報を取得
    const fetchRoomInfo = async () => {
      try {
        setIsLoadingRoom(true);
        const baseUrl = process.env.REACT_APP_URL || 'http://localhost:8081';
        const response = await fetch(`${baseUrl}/api/room/${state.roomid}`);
        
        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setRoomInfo({
            roomid: data.roomid,
            coupleName: data.coupleName
          });
        } else {
          console.error('ルーム情報の取得に失敗:', data.message);
          setRoomInfo({
            roomid: state.roomid,
            coupleName: state.roomid // フォールバック
          });
        }
      } catch (error) {
        console.error('ルーム情報取得エラー:', error);
        setRoomInfo({
          roomid: state.roomid,
          coupleName: state.roomid // フォールバック
        });
      } finally {
        setIsLoadingRoom(false);
      }
    };

    fetchRoomInfo();
  }, [state, navigate]);

  // 画面サイズに応じてサイドバー状態を設定
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const newIsDesktop = width >= 1024;
      setIsDesktop(newIsDesktop);
      
      if (newIsDesktop && !isDesktop) {
        setSidebarOpen(false);
      } else if (!newIsDesktop && isDesktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDesktop]);

  // デザイン名を取得
  const getDesignName = (designId: string, designType?: string): string => {
    if (designType === 'original' && designId === 'custom') {
      return 'オリジナルデザイン';
    }
    
    const designs = {
      elegant: 'エレガント',
      romantic: 'ロマンティック',
      welcome: 'ウェルカム'
    };
    return designs[designId as keyof typeof designs] || designId;
  };

  // 同意確認バリデーション
  const validateConsent = (): boolean => {
    const errors: ConsentErrors = {};
    
    if (!consentData.personalInfoConsent) {
      errors.personalInfoConsent = '個人情報の利用についてご同意ください';
    }
    
    if (!consentData.processingConsent) {
      errors.processingConsent = '個人情報の処理についてご同意ください';
    }
    
    setConsentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 確認画面への遷移処理
  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateConsent()) {
      return;
    }

    // 確認画面に遷移するためのデータを準備
    const navigationState = {
      ...state, // 既存の全データを保持
      consentData: consentData // 同意データも追加
    };

    console.log('📋 確認画面に遷移します:', navigationState);
    
    // 確認画面へ遷移
    navigate('/admin/qr-paper/confirm', { state: navigationState });
  };

  // 同意チェックボックスの更新
  const handleConsentChange = (field: keyof ConsentData, value: boolean) => {
    setConsentData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // エラーをクリア
    if (consentErrors[field]) {
      setConsentErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBack = () => {
    navigate('/admin/qr-paper/order', { 
      state: { 
        roomid: state.roomid, 
        qrUrl: state.qrUrl,
        selectedDesign: state.selectedDesign,
        designType: state.designType,
        originalDesignRequests: state.originalDesignRequests
      } 
    });
  };


  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarOverlayClick = () => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  };

  if (!state?.roomid) {
    return null; // リダイレクト処理中
  }

  return (
    <div className="wedding-admin-layout">
      {/* サイドバーオーバーレイ（モバイル時） */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}
      
      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>
      
      <div className={`wedding-main-content ${isDesktop ? 'desktop' : 'mobile'}`}>
        {/* トグルボタン（モバイル時のみ） */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <FileText size={24} />
          </button>
        )}

        {/* ヘッダーセクション */}
        <div className="wedding-header">
          <div className="wedding-header-decoration">
            <div className="crown-icon">
              <Crown size={32} />
            </div>
            <div className="sparkle-icon sparkle-1">
              <Sparkles size={20} />
            </div>
            <div className="sparkle-icon sparkle-2">
              <Sparkles size={16} />
            </div>
          </div>
          <h1 className="wedding-title">
            QRコード用紙作成
            <Heart className="title-heart" size={40} />
          </h1>
          <p className="wedding-subtitle">Privacy Consent - 個人情報の取り扱いについて</p>
        </div>

        {/* メインコンテンツ */}
        <div className="qr-paper-content">
          {/* 進行状況インジケーター */}
          <div className="progress-indicator">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">デザイン選択</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step completed">
              <div className="step-number">2</div>
              <div className="step-label">情報入力</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step active">
              <div className="step-number">3</div>
              <div className="step-label">同意確認</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">4</div>
              <div className="step-label">最終確認</div>
            </div>
          </div>



          {/* 個人情報利用目的カード */}
          <div className="wedding-card consent-info-card">
            <div className="card-header">
              <h2 className="card-title">個人情報の取り扱いについて</h2>
              <p className="qr-paper-card-description">お客様の個人情報を適切に管理し <br />以下の目的でのみ利用いたします</p>
            </div>

            <div className="consent-info-content">
              <div className="privacy-policy-container">
                <div className="privacy-policy-scroll">
                  <div className="privacy-policy-content">
                    <h3 className="privacy-policy-title">プライバシーポリシー</h3>
                    
                    <p className="privacy-policy-intro">
                      株式会社K.Platinum（以下、「当社」といいます。）は、本ウェブサイト上で提供するサービス（以下、「本サービス」といいます。）における、ユーザーの個人情報の取扱いについて、以下のとおりプライバシーポリシー（以下、「本ポリシー」といいます。）を定めます。
                    </p>

                    <div className="privacy-section">
                      <h4 className="privacy-section-title">第1条（個人情報）</h4>
                      <p className="privacy-section-content">
                        「個人情報」とは、個人情報保護法にいう「個人情報」を指すものとし、生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日、住所、電話番号、連絡先その他の記述等により特定の個人を識別できる情報及び容貌、指紋、声紋にかかるデータ、及び健康保険証の保険者番号などの当該情報単体から特定の個人を識別できる情報（個人識別情報）を指します。
                      </p>
                    </div>

                    <div className="privacy-section">
                      <h4 className="privacy-section-title">第2条（個人情報の収集方法）</h4>
                      <p className="privacy-section-content">
                        当社は、ユーザーが利用登録をする際に氏名、生年月日、住所、電話番号、メールアドレス、銀行口座番号、クレジットカード番号、運転免許証番号などの個人情報をお尋ねすることがあります。また、ユーザーと提携先などとの間でなされたユーザーの個人情報を含む取引記録や決済に関する情報を、当社の提携先（情報提供元、広告主、広告配信先などを含みます。以下、｢提携先｣といいます。）などから収集することがあります。
                      </p>
                    </div>

                    <div className="privacy-section">
                      <h4 className="privacy-section-title">第3条（個人情報を収集・利用する目的）</h4>
                      <p className="privacy-section-content">当社が個人情報を収集・利用する目的は、以下のとおりです。</p>
                      <ul className="privacy-list">
                        <li>当社サービスの提供・運営のため</li>
                        <li>ユーザーからのお問い合わせに回答するため（本人確認を行うことを含む）</li>
                        <li>ユーザーが利用中のサービスの新機能、更新情報、キャンペーン等及び当社が提供する他のサービスの案内のメールを送付するため</li>
                        <li>メンテナンス、重要なお知らせなど必要に応じたご連絡のため</li>
                        <li>利用規約に違反したユーザーや、不正・不当な目的でサービスを利用しようとするユーザーの特定をし、ご利用をお断りするため</li>
                        <li>ユーザーにご自身の登録情報の閲覧や変更、削除、ご利用状況の閲覧を行っていただくため</li>
                        <li>有料サービスにおいて、ユーザーに利用料金を請求するため</li>
                        <li>上記の利用目的に付随する目的</li>
                      </ul>
                    </div>

                    <div className="privacy-section">
                      <h4 className="privacy-section-title">第4条（個人情報の第三者提供）</h4>
                      <p className="privacy-section-content">
                        1.当社は、次に掲げる場合を除いて、あらかじめユーザーの同意を得ることなく、第三者に個人情報を提供することはありません。ただし、個人情報保護法その他の法令で認められる場合を除きます。
                      </p>
                      <ul className="privacy-list">
                        <li>人の生命、身体または財産の保護のために必要がある場合であって、本人の同意を得ることが困難であるとき</li>
                        <li>公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得ることが困難であるとき</li>
                        <li>国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき</li>
                        <li>予め次の事項を告知あるいは公表し、かつ当社が個人情報保護委員会に届出をしたとき
                          <ul className="privacy-sublist">
                            <li>利用目的に第三者への提供を含むこと</li>
                            <li>第三者に提供されるデータの項目</li>
                            <li>第三者への提供の手段または方法</li>
                            <li>本人の求めに応じて個人情報の第三者への提供を停止すること</li>
                            <li>本人の求めを受け付ける方法</li>
                          </ul>
                        </li>
                      </ul>
                      <p className="privacy-section-content">
                        2.前項の定めにかかわらず、次に掲げる場合には、当該情報の提供先は第三者に該当しないものとします。
                      </p>
                      <ul className="privacy-list">
                        <li>当社が利用目的の達成に必要な範囲内において個人情報の取扱いの全部または一部を委託する場合</li>
                        <li>合併その他の事由による事業の承継に伴って個人情報が提供される場合</li>
                        <li>個人情報を特定の者との間で共同して利用する場合であって、その旨並びに共同して利用される個人情報の項目、共同して利用する者の範囲、利用する者の利用目的および当該個人情報の管理について責任を有する者の氏名または名称について、あらかじめ本人に通知し、または本人が容易に知り得る状態に置いた場合</li>
                      </ul>
                    </div>

                    <div className="privacy-section">
                      <h4 className="privacy-section-title">第5条（個人情報の訂正および削除）</h4>
                      <p className="privacy-section-content">
                        ユーザーは、当社の保有する自己の個人情報が誤った情報である場合には、当社が定める手続きにより、当社に対して個人情報の訂正、追加または削除（以下、「訂正等」といいます。）を請求することができます。
                      </p>
                      <p className="privacy-section-content">
                        当社は、ユーザーから前項の請求を受けてその請求に応じる必要があると判断した場合には、遅滞なく、当該個人情報の訂正等を行うものとします。
                      </p>
                      <p className="privacy-section-content">
                        当社は、前項の規定に基づき訂正等を行った場合、または訂正等を行わない旨の決定をしたときは遅滞なく、これをユーザーに通知します。
                      </p>
                    </div>

                    <div className="privacy-section">
                      <h4 className="privacy-section-title">第6条（お問い合わせ窓口）</h4>
                      <p className="privacy-section-content">
                        本ポリシーに関するお問い合わせは、下記の窓口までお願いいたします。
                      </p>
                      <div className="contact-info">
                        <p>住所：〒124-0023 東京都葛飾区東新小岩2丁目23−17</p>
                        <p>社名：株式会社K.Platinum</p>
                        <p>代表取締役：沼田 海斗</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 同意確認カード */}
          <div className="wedding-card consent-form-card">
            <div className="card-header">
              <h2 className="card-title">同意確認</h2>
              <p className="qr-paper-card-description">内容に同意の上 <br />チェックしてください</p>
            </div>

            <form onSubmit={handleNext} className="consent-form">
              {/* 個人情報利用同意 */}
              <div className="consent-group">
                <label className="consent-checkbox-label">
                  <input
                    type="checkbox"
                    checked={consentData.personalInfoConsent}
                    onChange={(e) => handleConsentChange('personalInfoConsent', e.target.checked)}
                    className={`consent-checkbox ${consentErrors.personalInfoConsent ? 'error' : ''}`}
                  />
                  <span className="checkbox-text">
                    <strong>個人情報の利用目的について同意します</strong>
                    <span className="checkbox-description">
                      上記の利用目的でお客様の個人情報を使用することに同意いたします
                    </span>
                  </span>
                </label>
                {consentErrors.personalInfoConsent && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {consentErrors.personalInfoConsent}
                  </div>
                )}
              </div>

              {/* 個人情報処理同意 */}
              <div className="consent-group">
                <label className="consent-checkbox-label">
                  <input
                    type="checkbox"
                    checked={consentData.processingConsent}
                    onChange={(e) => handleConsentChange('processingConsent', e.target.checked)}
                    className={`consent-checkbox ${consentErrors.processingConsent ? 'error' : ''}`}
                  />
                  <span className="checkbox-text">
                    <strong>個人情報の処理・保管について同意します</strong>
                    <span className="checkbox-description">
                      個人情報の適切な管理・保管・削除について同意いたします
                    </span>
                  </span>
                </label>
                {consentErrors.processingConsent && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {consentErrors.processingConsent}
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* アクションボタン */}
          <div className="action-buttons">
            <button 
              onClick={handleBack}
              className="wedding-button secondary-button back-button"
            >
              <ArrowLeft size={20} />
              戻る
            </button>
            
            <button 
              onClick={handleNext}
              className={`wedding-button primary-button ${
                !consentData.personalInfoConsent || !consentData.processingConsent ? 'disabled' : ''
              }`}
              disabled={!consentData.personalInfoConsent || !consentData.processingConsent}
            >
              <ArrowRight size={20} />
              次へ
            </button>
          </div>
        </div>

        {/* フッター装飾 */}
        <div className="wedding-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Privacy Protected, Trusted Service</p>
        </div>
      </div>

    </div>
  );
};

export default QRPaperConsentForm;